import { isString } from 'underscore';

import { EmailTypeEnum, IScheduledEmailContent } from '../../../../types/working-model';
import { namePlaceholder, EmailContent, EmailDesign, EmailTextPhrases, EventEmailProps } from './event-emails';
import useEditableEmailFields from './use-editable-email-fields';
import AddToCalendar from './add-to-calendar';
import { OptionalComponent } from '../../../../utils/optional-component';
import { TextAlignmentsEnum } from '../../../../types/template-layouts';


export interface EmailContentProps {
	emailType: EmailTypeEnum;
	design: EmailDesign;
	emailContent: EmailContent;
	customContent?: IScheduledEmailContent;
	onEdit?: EventEmailProps["onEdit"];
	sent?: boolean;
	forceRtl?: boolean;
	hideCalendar?: boolean;
}

const SessionEmailContent = ({
	emailType,
	design,
	emailContent,
	customContent,
	onEdit,
	sent,
	forceRtl,
	hideCalendar
}: EmailContentProps): JSX.Element => {

	if (!emailContent.session) return (<></>);

	const textPhrase = (key: keyof Omit<EmailTextPhrases, 'subject'>) => {
		return emailContent.translations[key];
	};

	const reminderWording = () => {
		if (emailType === EmailTypeEnum.session_live) {
			return textPhrase('isLive');
		}
		if (emailType === EmailTypeEnum.session_ended) {
			return textPhrase('isEnded');
		}

		const days = emailContent.session?.daysToStart ?? 0;
		if (days < 1) {
			return `${textPhrase('starting')} ${textPhrase('soon')}`;
		} else if (days < 2) {
			return `${textPhrase('starting')} ${textPhrase('tomorrow')}`;
		} else {
			return `${textPhrase('startingIn')} ${days} ${textPhrase('days')}`;
		}
	};

	const initialButtonText = textPhrase(emailType === EmailTypeEnum.session_reminder ? 'SessionDetails' : 'WatchSession');
	const buttonText = (customContent?.button && isString(customContent.button)) ? customContent.button : initialButtonText;

	interface IEditableContent {
		salutation: JSX.Element;
		reminder: JSX.Element;
		sessionName: JSX.Element;
		sessionDescription: JSX.Element;
	}

	const editableContent: IEditableContent = {
		salutation: (<h4 style={design.styles.h4}><span style={design.styles.h4Span}>{textPhrase('salutation')} {namePlaceholder},</span></h4>),
		reminder: (<h3 style={design.styles.h3}><span style={design.styles.h3Span}>{emailContent.session.name} {reminderWording()}</span></h3>),
		sessionName: (<h3 style={design.styles.h3}><span style={design.styles.h3Span}>{emailContent.session.name}</span></h3>),
		sessionDescription: (<p style={design.styles.p}><span style={design.styles.pSpan}>{emailContent.session.description}</span></p>),
	};

	const {
		getCellContent,
		getButton,
		getImage,
		EditImageModal
	} = useEditableEmailFields<IEditableContent>({ onEdit, sent, design, emailContent, emailContentType: 'session', editableContent, customContent, buttonText });

	const alignment = (ltrLanguageAlign = TextAlignmentsEnum.center) => forceRtl ? TextAlignmentsEnum.right : ltrLanguageAlign;
	const direction = forceRtl ? 'rtl' : 'ltr';

	// FUTURE DEV - no speaker for now... will need to be conditional (not shown on session ended)
	// only display first three speakers
	// const displaySpeakers = emailContent.session.speakers?.slice(0, 3) ?? undefined;
	// const SpeakerCard = ({ name, image, social }: { name: string; image: string; social?: SocialLink[]; }): JSX.Element => {
	// 	return (
	// 		<td align={alignment()} dir={direction}>
	// 			<table>
	// 				<tr>
	// 					<td>
	// 						{image.length && <img
	// 							src={image}
	// 							alt="Speaker Image"
	// 							border="0"
	// 							width="170"
	// 							style={{ "maxWidth": "170px", "display": "block" }}
	// 						/>}
	// 					</td>
	// 				</tr>
	// 				<tr>
	// 					<td><span style={styles.caption}>{name}</span></td>
	// 				</tr>
	// 				<tr>
	// 				</tr>
	// 			</table>
	// 		</td>
	// 	);
	// };

	return (<>
		<tr>
			<td align={alignment(TextAlignmentsEnum.left)} dir={direction}>
				{getCellContent('salutation')}
			</td>
		</tr>
		<tr>
			<td align={alignment(TextAlignmentsEnum.left)} dir={direction}>
				{getCellContent('reminder')}
			</td>
		</tr>
		<tr>
			{getImage()}
		</tr>
		<OptionalComponent display={!!emailContent.session.start && emailContent.settings?.showStartTime}>
			<tr>
				<td align={alignment()} dir={direction}>
					<p style={{ ...design.styles.accent, ...design.styles.accentSpan }}>{emailContent.session.start}</p>
				</td>
			</tr>
		</OptionalComponent>
		<tr>
			<td align={alignment(TextAlignmentsEnum.left)} dir={direction}>
				{getCellContent('sessionName')}
			</td>
		</tr>
		<tr>
			<td align={alignment(TextAlignmentsEnum.left)} dir={direction}>
				{getCellContent('sessionDescription')}
			</td>
		</tr>
		<tr style={{ "height": "55px" }}>
			<td align={alignment()} dir={direction}>
				{getButton()}
			</td>
		</tr>
		<OptionalComponent display={emailContent.settings?.showAddCalendar && emailType === EmailTypeEnum.session_reminder && !hideCalendar}>
			<AddToCalendar
				emailContent={emailContent}
				design={design}
				emailType={emailType}
				customContent={customContent}
				onEdit={onEdit}
				sent={sent}
			/>
		</OptionalComponent>

		{EditImageModal}
	</>);
};

export default SessionEmailContent;